// 富文本展开/收起、FAQ 循环滚动（供无水合内联调用 / 客户端自动初始化）
export default defineNuxtPlugin((nuxtApp) => {
  if (typeof window === 'undefined') return

  if (!(window as any).__toggleRichText) {
    ;(window as any).__toggleRichText = (id: string) => {
      try {
        const content = document.getElementById(`rich-text-${id}`)
        if (!content) return

        const btn = document.querySelector(
          `.content-more[data-component-id="${id}"]`
        ) as HTMLElement | null

        const collapsedHeight = (content as HTMLElement).dataset?.collapsedHeight || '795'
        const expanded = btn?.getAttribute('data-expanded') === '1'

        if (!expanded) {
          content.style.height = 'auto'
          content.style.maxHeight = '100%'
          content.style.overflow = 'visible'
          if (btn) {
            btn.setAttribute('data-expanded', '1')
            btn.textContent = btn.dataset.packUp || '收起'
          }
        } else {
          content.style.maxHeight = `${collapsedHeight}px`
          content.style.overflow = 'hidden'
          if (btn) {
            btn.setAttribute('data-expanded', '0')
            btn.textContent = btn.dataset.viewMore || '查看更多'
            content.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }
      } catch (err) {
        console.error('toggleRichText error', id, err)
      }
    }
  }

  if ((window as any).__faqScrollSetup) return
  ;(window as any).__faqScrollSetup = true

  const FAQ_SCROLL_INTERVAL = 5000
  const FAQ_VISIBLE_COUNT = 2

  // 跟踪所有需要清理的资源，防止 SPA 导航后内存泄漏
  const cleanupFns: Array<() => void> = []

  const getItemHeight = (item: HTMLElement) => {
    const style = window.getComputedStyle(item)
    const marginBottom = parseFloat(style.marginBottom) || 0
    return item.offsetHeight + marginBottom
  }

  const initFAQScrollForViewport = (viewport: HTMLElement) => {
    if (viewport.getAttribute('data-faq-inited') === '1') return

    const totalCount = parseInt(viewport.getAttribute('data-faq-count') || '0', 10)
    if (totalCount <= FAQ_VISIBLE_COUNT) return

    const container = viewport.querySelector('#faq-list-container') as HTMLElement | null
    if (!container) return

    const items = container.querySelectorAll<HTMLElement>('.faq-item')
    if (items.length < totalCount) return

    viewport.setAttribute('data-faq-inited', '1')

    // 可视 2 条时，起始下标最大为 totalCount - 2；滚到末尾后回到顶部（不复制列表）
    const maxIndex = totalCount - FAQ_VISIBLE_COUNT
    let currentIndex = 0

    const setViewportHeight = () => {
      let height = 0
      for (let i = 0; i < FAQ_VISIBLE_COUNT; i++) {
        height += getItemHeight(items[i]!)
      }
      viewport.style.height = `${height}px`
    }

    const getOffsetForIndex = (index: number) => {
      let offset = 0
      for (let i = 0; i < index; i++) {
        offset += getItemHeight(items[i]!)
      }
      return offset
    }

    setViewportHeight()

    const scrollNext = () => {
      if (currentIndex >= maxIndex) {
        currentIndex = 0
      } else {
        currentIndex++
      }
      container.style.transform = `translateY(-${getOffsetForIndex(currentIndex)}px)`
    }

    let timer: ReturnType<typeof setInterval> | null = null

    const startScroll = () => {
      if (timer) return
      timer = setInterval(scrollNext, FAQ_SCROLL_INTERVAL)
    }

    const stopScroll = () => {
      if (!timer) return
      clearInterval(timer)
      timer = null
    }

    startScroll()

    const faqBox = viewport.closest('.FAQBox') || viewport
    faqBox.addEventListener('mouseenter', stopScroll)
    faqBox.addEventListener('mouseleave', startScroll)
    faqBox.addEventListener('touchstart', stopScroll, { passive: true })
    faqBox.addEventListener('touchend', startScroll)
    faqBox.addEventListener('touchcancel', startScroll)

    window.addEventListener('resize', setViewportHeight)

    // 注册清理函数，SPA 导航时移除所有监听器和定时器
    cleanupFns.push(() => {
      stopScroll()
      faqBox.removeEventListener('mouseenter', stopScroll)
      faqBox.removeEventListener('mouseleave', startScroll)
      faqBox.removeEventListener('touchstart', stopScroll)
      faqBox.removeEventListener('touchend', startScroll)
      faqBox.removeEventListener('touchcancel', startScroll)
      window.removeEventListener('resize', setViewportHeight)
    })
  }

  const tryInitFAQScroll = () => {
    const viewport = document.getElementById('faq-viewport')
    if (viewport) initFAQScrollForViewport(viewport)
  }

  // MutationObserver：等待 NuxtIsland 异步插入 FAQ 后再初始化；成功后断开
  const observer = new MutationObserver(() => {
    tryInitFAQScroll()
    if (document.getElementById('faq-viewport')?.getAttribute('data-faq-inited') === '1') {
      observer.disconnect()
    }
  })

  const startObserving = () => {
    observer.disconnect()
    observer.observe(document.body, { childList: true, subtree: true })
  }

  const cleanupFAQScroll = () => {
    observer.disconnect()
    while (cleanupFns.length > 0) {
      cleanupFns.pop()?.()
    }
    const viewport = document.getElementById('faq-viewport')
    if (viewport) {
      viewport.removeAttribute('data-faq-inited')
      viewport.style.height = ''
      const container = viewport.querySelector('#faq-list-container') as HTMLElement | null
      if (container) container.style.transform = ''
    }
  }

  const scheduleFAQScroll = () => {
    tryInitFAQScroll()
    // Island 常在 page:finish 之后才插入 DOM，未初始化成功则继续观察
    if (document.getElementById('faq-viewport')?.getAttribute('data-faq-inited') !== '1') {
      startObserving()
    }
  }

  tryInitFAQScroll()
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleFAQScroll)
  } else {
    startObserving()
  }

  // 离开页时清理；进入新页后再尝试初始化（不能在 page:finish 里直接 cleanup，否则会掐掉刚启动的滚动）
  nuxtApp.hook('page:start', cleanupFAQScroll)
  nuxtApp.hook('page:finish', scheduleFAQScroll)
  window.addEventListener('beforeunload', cleanupFAQScroll, { once: true })
})
